<script setup lang="ts">
const { t } = useTexts();

type Props = {
  page: number;
  total: number;
  perPage: number;
  lastPage: number;
  start: number;
  end: number;
};

const props = defineProps<Props>();

const activePage = ref(props.page);

function setPages() {
  const pages = [];

  // adiciona páginas anteriores
  if (props.page - 1 > 0) {
    pages.push(props.page - 1);
    if (props.page - 2 > 0) {
      pages.unshift(props.page - 2);
    }
  }

  // adiciona os pontinhos
  if (props.page - 4 > 0) {
    pages.unshift('dots');
  }

  // adiciona página atual
  pages.push(props.page);

  // adiciona páginas seguintes
  if (props.page + 1 <= props.lastPage) {
    pages.push(props.page + 1);
    if (props.page + 2 <= props.lastPage) {
      pages.push(props.page + 2);
    }
  }

  // adiciona os pontinhos
  if (props.page + 3 <= props.lastPage) {
    pages.push('dots');
  }

  return pages;
}

const items = ref(setPages());

const infoText = computed(() => {
  const start = props.start;
  const end = props.end;
  const total = props.total;

  return t('account.wallet.pagination.totalText', { start, end, total });
});

const showPrev = computed(() => {
  return props.page !== 1;
});

const showNext = computed(() => {
  return props.page !== props.lastPage;
});

const $emit = defineEmits(['changePage']);

function prev(first?: boolean) {
  if (first) {
    handleChangePage(1);

    return;
  }

  if (props.page - 1 > 0) {
    handleChangePage(props.page - 1);
  }
}

function next(last?: boolean) {
  if (last) {
    handleChangePage(props.lastPage);

    return;
  }
  if (props.page + 1 <= props.lastPage) {
    handleChangePage(props.page + 1);
  }
}

function disableItem(item: any) {
  return item === 'dots' || item === props.page;
}

function handleChangePage(page: any) {
  $emit('changePage', { page });
}

function goToSpecificPage(page: any) {
  if (page <= 0 || page > props.lastPage || isNaN(Number(page))) return;
  handleChangePage(page);
}
</script>

<template>
  <div class="paginationWrapper">
    <div class="paginationNavWrapper">
      <nav
        aria-label="Page navigation"
        class="paginationNav"
      >
        <ul class="paginationNavList">
          <li>
            <button
              v-if="showPrev"
              class="paginationBtn paginationPrev"
              :disabled="!showPrev"
              @click="prev(true)"
            >
              «
            </button>
          </li>
          <li class="hiddenMobile">
            <button
              v-if="showPrev"
              class="paginationBtn"
              :disabled="!showPrev"
              @click="prev()"
            >
              <span>‹</span>
            </button>
          </li>
          <li
            v-for="item in items"
            :key="item"
            @click="handleChangePage(item)"
          >
            <button
              class="paginationBtn"
              :class="[
                { active: item === props.page },
                { dots: item === 'dots' },
              ]"
              :disabled="disableItem(item)"
            >
              <span v-if="item != 'dots'">{{ item }}</span>
              <span v-else>...</span>
            </button>
          </li>
          <li class="hiddenMobile">
            <button
              v-if="showNext"
              class="paginationBtn"
              :disabled="!showNext"
              @click="next()"
            >
              <span>›</span>
            </button>
          </li>
          <li>
            <button
              v-if="showNext"
              class="paginationBtn paginationNext"
              :disabled="!showNext"
              @click="next(true)"
            >
              »
            </button>
          </li>
        </ul>
      </nav>

      <form
        class="paginationDirect"
        @submit.prevent="goToSpecificPage(activePage)"
      >
        <input
          v-model="activePage"
          class="paginationDirect-input"
          type="text"
        />
        <button class="paginationBtn">
          <NuxtIcon name="magic" />
        </button>
      </form>
    </div>
    <div class="paginationInfo">
      {{ infoText }}
    </div>
  </div>
</template>

<style scoped lang="scss">
.paginationWrapper {
  @apply md:flex justify-center md:justify-between md:items-center py-2 px-2 text-left text-xs font-semibold whitespace-nowrap bg-primary/5 text-primary/70 rounded;

  .paginationInfo {
    @apply text-2xs mt-3 md:mt-0 px-2 text-center md:text-left;
  }

  .paginationNavWrapper {
    @apply flex justify-center items-center gap-3 flex-wrap md:gap-2;

    .paginationBtn {
      @apply flex items-center justify-center px-3 h-8 leading-tight text-texts bg-bg-primary/50 rounded;

      &.active,
      &:hover {
        @apply text-primary bg-primary/50;
      }

      &.dots,
      &.dots:hover {
        @apply bg-bg-primary/30 text-texts/30;
      }
    }

    .paginationNav {
      @apply flex justify-center;

      .paginationNavList {
        @apply flex items-center -space-x-px gap-1;

        .hiddenMobile {
          @apply hidden md:inline;
        }
        .paginationPrev,
        .paginationNext {
          @apply ml-0 leading-tight text-texts;
        }
      }
    }

    .paginationDirect {
      @apply flex;

      &-input {
        @apply w-10 h-8 bg-bg-primary/70 text-primary text-center rounded text-sm;
      }

      .paginationBtn {
        @apply bg-button-bg text-button-text;
      }
    }
  }
}
</style>
